import {
  type DependencyList,
  type Key,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDeepCompareEffect, usePrevious, useSafeState } from 'ahooks';
import { useMemoizedFn } from 'ahooks';
import { Button, TreeSelect, type TreeSelectProps } from 'antd';
import type { SafeKey } from 'antd/es/table/interface.js';
import { CaretDownFilled, RedoOutlined } from '@ant-design/icons';
import { isArray, isObject } from '@dimjs/lang';
import { classNames, extend, get } from '@dimjs/utils';
import {
  getUuid,
  isNotEmptyArray,
  isUndefinedOrNull,
  type TAny,
  toArray,
  type TPlainObject,
  treeToArray,
  treeToTiledMap,
} from '@flatbiz/utils';
import { fbaHooks } from '../fba-hooks/index.js';
import {
  RequestStatus,
  type TRequestStatusProps,
} from '../request-status/index.js';
import { treeSelectorWrapperModel } from './model.js';
import { getExpandedKeysByValues } from './utils.js';
import './style.less';

type TreeSelectorWrapperValue =
  | string
  | number
  | Array<string | number>
  | TPlainObject<string | number>
  | Array<TPlainObject<string | number>>;

type TreeSelectorServiceConfig = {
  params?: TPlainObject;
  requiredParamsKeys?: string[];
  onRequest?: (params?: TAny) => TAny;
  /**
   * 响应数据适配器
   */
  onRequestResultAdapter?: (respData: TAny) => TPlainObject[];
};

export type TreeSelectorWrapperProps = Omit<
  TreeSelectProps,
  | 'treeExpandedKeys'
  | 'treeData'
  | 'loading'
  | 'onTreeExpand'
  | 'onChange'
  | 'value'
  | 'fieldNames'
  | 'defaultValue'
  | 'treeDataSimpleMode'
  | 'onSearch'
> & {
  /**
   * 设置请求唯一值
   * ```
   * 1. 设置后，如果请求入参不变，不会重复请求接口，直接使用上次缓存数据
   * ```
   */
  modelKey?: string;
  fieldNames?: {
    /**
     * 1. list 为 onRequest 返回数据中列表key值，可多级取值，例如： 'a.b.c'
     * 2. 配置 serviceConfig.onRequestResultAdapter 后此配置失效
     */
    list?: string;
    label?: string;
    value?: string;
    children?: string;
    disabled?: string;
  };
  /**
   * useEffect依赖项数组，用于重新发起获取接口数据
   */
  effectDependencyList?: DependencyList;
  /**
   * 请求服务需求的数据，当设置`treeSelectorList`后无效果
   */
  serviceConfig?: TreeSelectorServiceConfig;
  /**
   * 当设置treeSelectorList后，serviceConfig 将失效
   */
  treeSelectorList?: TreeSelectProps['treeData'];
  /**
   * treeSelectorList发生变更时触发
   */
  onTreeSelectorListChange?: (
    treeSelectorList?: TreeSelectProps['treeData']
  ) => void;
  /**
   * 通过服务获取数据异常回调，当设置`treeSelectorList`后无效果
   */
  onTreeSelectorRequestError?: (error: Error) => void;
  /**
   * 添加全部选项
   * ```
   * 1. showAllOption = true，添加默认全部选项（value值为空字符串）
   * 2. 可自定义全部选项，例如：{ label: '全部', value: 'all' }
   * 3. 自定义字段会通过fieldNames转换后使用
   * 4. 多选操作时，最好不要设置全部选项
   * ```
   */
  showAllOption?: true | TPlainObject<string | number>;
  /**
   * 1. 当 treeCheckable = true && treeCheckStrictly = true，如果选择的数据中含有父节点，selectedValue、selectedList返回数据包含父节点
   * 2. 当 treeCheckable = true && treeCheckStrictly = false，selectedValue、selectedList返回的数据不包含父节点
   * 3. 当 treeCheckable = false，节点选择没有级联关系，selectedValue、selectedList返回的数据就是实际选择
   * 4. selectAllDataList 返回的数据都包含父节点
   * 5. 使用 onTreeItemAdapter 拼接label数据后，选中回填数据也是拼接后的
   * 6. triggerInfo 当前操作节点数据
   */
  onChange?: (
    selectedValue?: TreeSelectorWrapperValue,
    selectedData?: TPlainObject[] | TPlainObject,
    triggerInfo?: TPlainObject
  ) => void;
  /**
   * tree item 数据适配器，返回的数据会通过fieldNames配置取数据
   * ```
   * 1. 可用于设置disabled状态
   * onTreeItemDataAdapter: (dataItem) => {
   *   if(dataItem.xx === xx){
   *     dataItem['disabled] = true;
   *   }
   *   return dataItem;
   * }
   * 2. 可设置label数据显示
   * ```
   */
  onTreeItemDataAdapter?: (dataItem: TPlainObject) => TPlainObject;
  // icon 无法实现、treeIcon不起作用4.20.0
  // showIcon?: boolean;
  // icon?: (data: TPlainObject) => ReactElement;
  /** 自定义异常提示文案 */
  requestMessageConfig?: TRequestStatusProps['messageConfig'];
  /**
   * value格式
   *```
   * 1. string 、number
   * 2. Array<string | number>
   * 3. lableInValue = true，根据labelInValueFieldNames配置格式
   * 4. lableInValue = true，Array<labelInValueFieldNames配置>
   * ```
   */
  value?: TreeSelectorWrapperValue;

  /**
   * treeCheckStrictly 	checkable 状态下节点选择完全受控（父子节点选中状态不再关联），
   * 会使得 labelInValue 强制为 true
   */
  labelInValue?: boolean;
  /**
   * treeCheckStrictly = true模式下有效，点击父节点是否选中所有子节点，默认值：true
   */
  selectedParentCheckedAllChildrenList?: boolean;
  /** 有唯一跟节点时，初始化是否展开，默认值：false */
  initRootExpand?: boolean;

  /**
   * 在第一次渲染value时，主动执行一次onChange
   * ```
   * 1. 设置labelInValue = true 时有效
   * 2. 不考虑子父节点的关系
   * ```
   */
  executeOnChangeInRenderFirstValue?: boolean;
};

/**
 * 树选择器包装组件，由于过度封装，部分antd TreeSelect原始功能不支持，不满足情况的请使用antd TreeSelect实现
 * ```
 * 1. 当设置treeSelectorList属性后，serviceConfig、onTreeSelectorListChange将失效
 * 2. 父节点默认不返回，需要返回请设置showCheckedStrategy
 * ```
 */
export const TreeSelectorWrapper = (props: TreeSelectorWrapperProps) => {
  const {
    serviceConfig,
    effectDependencyList,
    onTreeSelectorListChange,
    onTreeSelectorRequestError,
    treeSelectorList,
    requestMessageConfig,
    modelKey,
    value,
    labelInValue: _labelInValue,
    onTreeItemDataAdapter,
    selectedParentCheckedAllChildrenList = true,
    fieldNames,
    onChange,
    treeDefaultExpandAll,
    showAllOption,
    initRootExpand,
    treeDefaultExpandedKeys,
    executeOnChangeInRenderFirstValue,
    ...otherProps
  } = props;
  /**
   * 不能使用key、defaultValue模式
   * 1. 勾选的和回填选中不相同情况，勾选后会渲染defaultValue模式后，弹框被关闭，操作体验存在问题
   */
  const hasTreeSelectorList = Object.prototype.hasOwnProperty.call(
    props,
    'treeSelectorList'
  );

  const modelKeyInner = useMemo(() => {
    return modelKey || getUuid();
  }, [modelKey]);

  const newServiceConfig = serviceConfig || {};
  const newEffectDependencyList = effectDependencyList || [];
  const [loading, setLoading] = useSafeState(false);
  const [treeExpandedKeys, setTreeExpandedKeys] = useState<Key[]>();
  const [state, actions] = treeSelectorWrapperModel(modelKeyInner).useStore();
  const requestPreKey = `request-progress-${modelKeyInner}`;
  const isMultiple = otherProps.treeCheckable || otherProps.multiple;
  const responseFirstRef = useRef(true);
  /**
   * treeCheckStrictly 	checkable 状态下节点选择完全受控（父子节点选中状态不再关联），
   * 会使得 labelInValue 强制为 true
   */
  const labelInValue = useMemo(() => {
    if (otherProps.treeCheckStrictly) return true;
    return _labelInValue;
  }, [_labelInValue, otherProps.treeCheckStrictly]);

  const fieldNamesMerge = useMemo(() => {
    return {
      label: 'label',
      value: 'value',
      children: 'children',
      disabled: 'disabled',
      ...fieldNames,
    };
  }, [fieldNames]);

  const labelInValueFieldNamesMerge = useMemo(() => {
    return { label: 'label', value: 'value' };
  }, []);

  const allOptionConfig = useMemo(() => {
    if (showAllOption) {
      const isTrue = showAllOption === true;
      return {
        label: isTrue ? '全部' : showAllOption.label,
        value: isTrue ? '' : showAllOption.value,
      };
    }
    return null;
  }, [showAllOption]);

  const [searchValue, setSearchValue] = useState<string>();

  const propsPureValue = fbaHooks.useMemoCustom(() => {
    return toArray<TAny>(value).map((item) => {
      if (isObject(item)) {
        return (
          labelInValue
            ? item[labelInValueFieldNamesMerge.value]
            : item[fieldNamesMerge.value]
        ) as string | number;
      }
      return item as string | number;
    });
  }, [
    fieldNamesMerge.value,
    labelInValue,
    labelInValueFieldNamesMerge.value,
    props.value,
  ]);

  const prevPropsPureValue = usePrevious(propsPureValue);

  fbaHooks.useEffectCustom(() => {
    if (state.treeSelectorList.length > 0) {
      if (
        responseFirstRef.current &&
        executeOnChangeInRenderFirstValue &&
        labelInValue
      ) {
        const dataList: TPlainObject[] = [];
        const originalList: TPlainObject[] = [];
        propsPureValue.forEach((item) => {
          const target = state.treeSelectorTiledMap[item];
          if (target) {
            originalList.push(target);
            dataList.push({
              [labelInValueFieldNamesMerge.label]:
                target[fieldNamesMerge.label],
              [labelInValueFieldNamesMerge.value]:
                target[fieldNamesMerge.value],
            });
          }
        });
        if (isMultiple) {
          props.onChange?.(dataList ? dataList : undefined, originalList);
        } else {
          props.onChange?.(dataList ? dataList[0] : undefined, originalList[0]);
        }
      }
      let treeExpandedKeys: Array<string | number> = [];
      if (responseFirstRef.current) {
        responseFirstRef.current = false;
        if (treeDefaultExpandedKeys) {
          treeExpandedKeys = treeDefaultExpandedKeys as Array<string | number>;
        } else if (treeDefaultExpandAll) {
          const allValues = Object.keys(state.treeSelectorTiledMap).map(
            (item) =>
              state.treeSelectorTiledMap[item]?.[fieldNamesMerge.value] as
                | string
                | number
          );
          treeExpandedKeys = allValues;
        } else if (initRootExpand && state.treeSelectorList.length === 1) {
          treeExpandedKeys = [state.treeSelectorList[0][fieldNamesMerge.value]];
        }
        setTreeExpandedKeys(treeExpandedKeys);
      }
      //  else {
      //   responseFirstRef.current = false;
      //   if (!searchValue) {
      //     const expandedKeys = getExpandedKeysByValues(
      //       propsPureValue,
      //       state.treeSelectorTiledMap
      //     );
      //     setTreeExpandedKeys(expandedKeys);
      //   }
      // }
    }
  }, [propsPureValue, state.treeSelectorList]);

  fbaHooks.useEffectCustom(() => {
    if (!responseFirstRef.current && propsPureValue.length > 0) {
      if (
        state.treeSelectorList.length > 0 &&
        JSON.stringify(propsPureValue) !== JSON.stringify(prevPropsPureValue)
      ) {
        if (!searchValue) {
          const expandedKeys = getExpandedKeysByValues(
            propsPureValue,
            state.treeSelectorTiledMap
          );
          setTreeExpandedKeys(expandedKeys);
        }
      }
    }
  }, [propsPureValue, state.treeSelectorList]);

  const valueIsEmpty = (value: string | number) => {
    return value === '' || isUndefinedOrNull(value);
  };

  const serviceResponseHandle = (respData) => {
    let result = respData;
    if (newServiceConfig.onRequestResultAdapter) {
      result = newServiceConfig.onRequestResultAdapter(
        respData as unknown as TPlainObject
      );
    } else if (fieldNamesMerge.list) {
      result = get(respData, fieldNamesMerge.list);
    }
    if (result && !isArray(result)) {
      console.warn('待渲染数据为非数组结构', result);
      return [];
    }
    return result || [];
  };

  const startDataSourceRequest = useMemoizedFn(async () => {
    if (!newServiceConfig.onRequest) {
      throw new Error('onRequest 调用接口服务不能为空');
    }
    const requiredParamsKeys = newServiceConfig.requiredParamsKeys || [];
    const params = extend({}, newServiceConfig.params);
    const isEmpty = requiredParamsKeys.find((key) => {
      return valueIsEmpty(params[key] as string | number);
    });
    if (isEmpty) {
      void actions.changeRequestStatus('no-dependencies-params');
      console.warn(
        `TreeSelectorWrapper组件：参数：${requiredParamsKeys.join('、')}不能为空`
      );
      return;
    }
    try {
      setLoading(true);
      window[requestPreKey] = true;
      void actions.changeRequestStatus('request-progress');
      const _respData = await newServiceConfig.onRequest?.(params);
      const respData = serviceResponseHandle(
        _respData
      ) as TreeSelectProps['treeData'];
      setLoading(false);
      window[requestPreKey] = false;
      onChangeSelectorList(respData || []);
    } catch (error: TAny) {
      setLoading(false);
      window[requestPreKey] = false;
      void actions.changeRequestStatus('request-error');
      onTreeSelectorRequestError?.(error);
    }
  });

  useDeepCompareEffect(() => {
    if (hasTreeSelectorList) return;
    // 当无依赖项时，如果存在缓存数据，就不在调用接口
    if (newEffectDependencyList.length) {
      // 内部主动清楚数据，被依赖的数据发生变更时，依赖组件数据清空
      onChangeSelectorList([]);
      void startDataSourceRequest();
      return;
    }
    const allState = treeSelectorWrapperModel(modelKeyInner).getState();
    if (allState.requestStatus === 'request-success') {
      return;
    }
    // 判断相同的modelKey是否已经在请求数据中，避免重复请求
    if (!window[requestPreKey]) {
      void startDataSourceRequest();
      return;
    }
  }, newEffectDependencyList);

  const onChangeSelectorList = useMemoizedFn((dataList: TAny[]) => {
    if (dataList?.length === 0 && state.treeSelectorList.length === 0) {
      void actions.setSelectBoxList({
        treeSelectorList: [],
        treeSelectorTiledMap: {},
      });
      onTreeSelectorListChange?.([]);
      return;
    }
    // 全部选项
    const tempItem = allOptionConfig
      ? {
          [fieldNamesMerge.label]: allOptionConfig.label,
          [fieldNamesMerge.value]: allOptionConfig.value,
        }
      : undefined;
    const newdataList = showAllOption ? [tempItem, ...dataList] : dataList;
    void actions.setSelectBoxList({
      treeSelectorList: newdataList,
      treeSelectorTiledMap: treeToTiledMap(
        newdataList,
        {
          value: fieldNamesMerge.value,
          children: fieldNamesMerge.children,
        },
        'pId'
      ),
    });
    onTreeSelectorListChange?.(dataList);
  });

  fbaHooks.useEffectCustom(() => {
    if (hasTreeSelectorList) {
      onChangeSelectorList(treeSelectorList || []);
    }
  }, [treeSelectorList]);

  const onTreeExpand = useMemoizedFn((expandedKeys) => {
    setTreeExpandedKeys(expandedKeys as string[]);
  });

  const onAgainRequest = useMemoizedFn(() => {
    void startDataSourceRequest();
  });

  /**
   * 根据treeCheckable、treeCheckStrictly、showCheckedStrategy等设置情况，判断返回数据是否包含父节点
   * 1. 当 treeCheckable = true，changeValue 数据格式为 { label,value }[]
   */
  const onInnerChange = useMemoizedFn(
    (changeValue: TAny, _data, triggerInfo: TPlainObject) => {
      let resultValueList = toArray<TAny>(changeValue);
      if (otherProps.treeCheckStrictly) {
        if (selectedParentCheckedAllChildrenList && triggerInfo.checked) {
          const target = state.treeSelectorTiledMap[triggerInfo?.triggerValue];
          if (isNotEmptyArray(target[fieldNamesMerge.children])) {
            resultValueList = treeToArray(
              [target],
              fieldNamesMerge.children
            ).map((item) => {
              return item[fieldNamesMerge.value];
            });
          } else {
            resultValueList = changeValue?.map((item) => item.value);
          }
        } else {
          resultValueList = changeValue?.map((item) => item.value);
        }
      }
      if (labelInValue) {
        const resultItemList = resultValueList.map((item) => {
          const target = state.treeSelectorTiledMap[item];
          return {
            [labelInValueFieldNamesMerge.label]: target[fieldNamesMerge.label],
            [labelInValueFieldNamesMerge.value]: target[fieldNamesMerge.value],
          };
        });
        if (isMultiple) {
          onChange?.(resultItemList, resultItemList, triggerInfo);
        } else {
          onChange?.(resultItemList[0], resultItemList[0], triggerInfo);
        }
      } else {
        const resultItemList = resultValueList.map((item) => {
          return state.treeSelectorTiledMap[item];
        });
        if (isMultiple) {
          onChange?.(resultValueList, resultItemList, triggerInfo);
        } else {
          onChange?.(resultValueList[0], resultItemList[0], triggerInfo);
        }
      }
    }
  );

  const mapTree = useMemoizedFn((data) => {
    if (!data) return null;
    return data.map((item) => {
      const itemAdapterData = onTreeItemDataAdapter?.({ ...item }) || item;
      const children = itemAdapterData[fieldNamesMerge.children];
      const value = itemAdapterData[fieldNamesMerge.value];
      const label = itemAdapterData[fieldNamesMerge.label];
      const disabled = itemAdapterData[fieldNamesMerge.disabled]
        ? itemAdapterData[fieldNamesMerge.disabled]
        : itemAdapterData.disabled;
      return (
        <TreeSelect.TreeNode
          {...itemAdapterData}
          disabled={disabled}
          value={value}
          title={label}
          key={`${value}`}
        >
          {children && children.length > 0 && mapTree(children)}
        </TreeSelect.TreeNode>
      );
    });
  });

  const onSearch = (searchValue?: string) => {
    setSearchValue(searchValue);
    let targetKeyList: Array<string | number> = [];
    if (!searchValue) {
      setSearchValue(undefined);
      targetKeyList = propsPureValue;
    } else {
      Object.keys(state.treeSelectorTiledMap).forEach((item) => {
        const target = state.treeSelectorTiledMap[item];
        const label = target?.[fieldNamesMerge.label] as string;
        if (label?.includes(searchValue)) {
          targetKeyList.push(target[fieldNamesMerge.value]);
        }
      });
    }

    const expandedKeysMerge = getExpandedKeysByValues(
      targetKeyList,
      state.treeSelectorTiledMap
    );
    setTreeExpandedKeys(expandedKeysMerge);
  };

  const treeValue = isMultiple ? propsPureValue : propsPureValue[0];

  /**
   *  1. 使用 TreeNode 渲染节点，不能重写 fieldNames 配置
   *  2. 使用 TreeNode 是为了实现 TreeNode ICON
   */
  return (
    <TreeSelect
      searchValue={searchValue}
      // dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
      showSearch={true}
      treeLine={{ showLeafIcon: false }}
      treeNodeFilterProp="title"
      switcherIcon={<CaretDownFilled />}
      popupMatchSelectWidth={false}
      {...otherProps}
      styles={{
        ...otherProps.styles,
        popup: {
          ...otherProps.styles?.popup,
          root: {
            maxHeight: 400,
            overflow: 'auto',
            ...otherProps.styles?.popup?.root,
          },
        },
      }}
      className={classNames('v-tree-select-wrapper', otherProps.className)}
      classNames={{
        popup: {
          ...otherProps.classNames?.popup,
          root: classNames(
            'v-tree-select-wrapper-dropdown-root',
            otherProps.classNames?.popup?.root
          ),
        },
      }}
      onChange={onInnerChange}
      treeExpandedKeys={treeExpandedKeys as SafeKey[]}
      value={treeValue}
      onSearch={onSearch}
      loading={loading}
      onTreeExpand={onTreeExpand}
      style={{ width: '100%', ...otherProps.style }}
      suffixIcon={
        state.requestStatus === 'request-error' ? (
          <RedoOutlined spin={loading} onClick={onAgainRequest} />
        ) : undefined
      }
      notFoundContent={
        <RequestStatus
          status={state.requestStatus}
          messageConfig={requestMessageConfig}
          loading={loading}
          errorButton={
            <Button type="primary" onClick={onAgainRequest}>
              重新获取数据
            </Button>
          }
        />
      }
    >
      {mapTree(state.treeSelectorList)}
    </TreeSelect>
  );
};
